import { useState } from 'react';
import { useGame } from '../GameContext';
import { Team, SoldierType } from '../types';
import api from '../services/api';
import './Admin.css';

const Admin: React.FC = () => {
  const { gameState, addPlayer, removePlayer, updatePlayerRaidPoints, switchPlayerTeam, addSoldierToArmy, removeSoldierFromArmy } = useGame();
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerTeam, setNewPlayerTeam] = useState<Team>('raiders');
  const [expandedRaiderId, setExpandedRaiderId] = useState<string | null>(null);
  const [expandedDefenderId, setExpandedDefenderId] = useState<string | null>(null);
  const [battlefieldBgUrl, setBattlefieldBgUrl] = useState(gameState.battlefieldBackgroundUrl || '');
  const [isSavingBg, setIsSavingBg] = useState(false);
  const [kingRaidersUrl, setKingRaidersUrl] = useState(gameState.kingRaidersImageUrl || '');
  const [kingDefendersUrl, setKingDefendersUrl] = useState(gameState.kingDefendersImageUrl || '');
  const [isSavingKings, setIsSavingKings] = useState(false);

  const handleAddPlayer = () => {
    if (newPlayerName.trim()) {
      addPlayer(newPlayerName.trim(), newPlayerTeam);
      setNewPlayerName('');
    }
  };

  const handleUpdateRaidPoints = (playerId: string, value: string) => {
    const points = parseInt(value) || 0;
    updatePlayerRaidPoints(playerId, points);
  };

  const handleIncrement = (playerId: string, soldierType: SoldierType) => {
    const success = addSoldierToArmy(playerId, soldierType);
    if (!success) {
      alert('Not enough raid points!');
    }
  };

  const handleDecrement = (playerId: string, soldierType: SoldierType) => {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return;

    let soldierToRemove = null;

    switch (soldierType) {
      case 'Knight':
        soldierToRemove = player.army.knights[player.army.knights.length - 1];
        break;
      case 'Archer':
        soldierToRemove = player.army.archers[player.army.archers.length - 1];
        break;
      case 'Crossbow':
        soldierToRemove = player.army.crossbows[player.army.crossbows.length - 1];
        break;
      case 'Healer':
        soldierToRemove = player.army.healers[player.army.healers.length - 1];
        break;
      case 'Siege Equipment':
        soldierToRemove = player.army.siegeEquipment[player.army.siegeEquipment.length - 1];
        break;
    }

    if (soldierToRemove) {
      removeSoldierFromArmy(playerId, soldierType, soldierToRemove.id);
    }
  };

  const getUnitCount = (playerId: string, soldierType: SoldierType): number => {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return 0;

    switch (soldierType) {
      case 'Knight': return player.army.knights.length;
      case 'Archer': return player.army.archers.length;
      case 'Crossbow': return player.army.crossbows.length;
      case 'Healer': return player.army.healers.length;
      case 'Siege Equipment': return player.army.siegeEquipment.length;
      default: return 0;
    }
  };

  const getTotalUnits = (playerId: string): number => {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return 0;

    return (
      player.army.knights.length +
      player.army.archers.length +
      player.army.crossbows.length +
      player.army.healers.length +
      player.army.siegeEquipment.length
    );
  };

  const getTeamManpower = (team: Team) => {
    const teamPlayers = gameState.players.filter(p => p.team === team);
    const totalUnits = teamPlayers.reduce((sum, player) => {
      return sum + (player.army.king ? 1 : 0) + getTotalUnits(player.id);
    }, 0);
    return { players: teamPlayers.length, units: totalUnits };
  };

  const getPlayerStats = (playerId: string): { hp: number; damage: number } => {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return { hp: 0, damage: 0 };

    let hp = 0;
    let damage = 0;

    if (player.army.king) {
      hp += gameState.unitStats['King'].hp;
      damage += gameState.unitStats['King'].damage;
    }

    player.army.knights.forEach(() => {
      hp += gameState.unitStats['Knight'].hp;
      damage += gameState.unitStats['Knight'].damage;
    });

    player.army.archers.forEach(() => {
      hp += gameState.unitStats['Archer'].hp;
      damage += gameState.unitStats['Archer'].damage;
    });

    player.army.crossbows.forEach(() => {
      hp += gameState.unitStats['Crossbow'].hp;
      damage += gameState.unitStats['Crossbow'].damage;
    });

    player.army.healers.forEach(() => {
      hp += gameState.unitStats['Healer'].hp;
      damage += gameState.unitStats['Healer'].damage;
    });

    player.army.siegeEquipment.forEach(() => {
      hp += gameState.unitStats['Siege Equipment'].hp;
      damage += gameState.unitStats['Siege Equipment'].damage;
    });

    return { hp, damage };
  };

  const getTeamStats = (team: Team): { hp: number; damage: number } => {
    const teamPlayers = gameState.players.filter(p => p.team === team);
    return teamPlayers.reduce(
      (totals, player) => {
        const stats = getPlayerStats(player.id);
        return {
          hp: totals.hp + stats.hp,
          damage: totals.damage + stats.damage
        };
      },
      { hp: 0, damage: 0 }
    );
  };

  const raiders = gameState.players.filter(p => p.team === 'raiders');
  const defenders = gameState.players.filter(p => p.team === 'defenders');
  const raidersManpower = getTeamManpower('raiders');
  const defendersManpower = getTeamManpower('defenders');
  const raidersStats = getTeamStats('raiders');
  const defendersStats = getTeamStats('defenders');

  const handleSwitchTeam = async (playerId: string) => {
    try {
      await switchPlayerTeam(playerId);
    } catch (err) {
      console.error('Failed to switch team:', err);
      alert(`Failed to switch team: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleUpdateBattlefieldBackground = async () => {
    setIsSavingBg(true);
    try {
      await api.updateBattlefieldBackground(battlefieldBgUrl);
      alert('Battlefield background updated successfully!');
      window.location.reload(); // Refresh to show the new background
    } catch (err) {
      console.error('Failed to update battlefield background:', err);
      alert(`Failed to update battlefield background: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsSavingBg(false);
    }
  };

  const handleUpdateKingImages = async () => {
    setIsSavingKings(true);
    try {
      await api.updateKingImages(kingRaidersUrl, kingDefendersUrl);
      alert('King images updated successfully!');
      window.location.reload(); // Refresh to show the new images
    } catch (err) {
      console.error('Failed to update King images:', err);
      alert(`Failed to update King images: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsSavingKings(false);
    }
  };

  const renderPlayerCard = (player: typeof raiders[0]) => (
    <div key={player.id} className={`player-card ${player.team}`}>
      <div className="player-header">
        <div className="player-info">
          <h3>{player.name}</h3>
          <span className="units-badge">{getTotalUnits(player.id)} units</span>
        </div>
        <div className="player-actions">
          <button onClick={() => handleSwitchTeam(player.id)} className="switch-team-btn" title="Switch to other team">
            ⇄
          </button>
          <button onClick={() => removePlayer(player.id)} className="delete-btn">Delete</button>
        </div>
      </div>

      <div className="raid-points-control">
        <label>Raid Points (Total):</label>
        <input
          type="number"
          value={player.raidPoints}
          onChange={(e) => handleUpdateRaidPoints(player.id, e.target.value)}
          min="0"
        />
        <span className="points-info">
          Spent: {player.raidPointsSpent} | Remaining: {player.raidPoints - player.raidPointsSpent}
        </span>
      </div>

      <button
        onClick={() => {
          if (player.team === 'raiders') {
            setExpandedRaiderId(expandedRaiderId === player.id ? null : player.id);
          } else {
            setExpandedDefenderId(expandedDefenderId === player.id ? null : player.id);
          }
        }}
        className="toggle-army-btn"
      >
        {((player.team === 'raiders' && expandedRaiderId === player.id) ||
          (player.team === 'defenders' && expandedDefenderId === player.id))
          ? '▼ Hide Army Management' : '▶ Manage Army'}
      </button>

      {((player.team === 'raiders' && expandedRaiderId === player.id) ||
        (player.team === 'defenders' && expandedDefenderId === player.id)) && (
        <div className="army-management">
          <div className="army-grid">
            <div className="unit-counter">
              <div className="unit-label">
                <span className="unit-icon">👑</span>
                <span>King</span>
                <span className="unit-info">(Free)</span>
              </div>
              <div className="counter-display">
                <span className="count">1</span>
              </div>
            </div>

            <div className="unit-counter">
              <div className="unit-label">
                <span className="unit-icon">⚔️</span>
                <span>Knights</span>
                <span className="unit-info">({gameState.unitStats['Knight'].cost} pts)</span>
              </div>
              <div className="counter-controls">
                <button
                  onClick={() => handleDecrement(player.id, 'Knight')}
                  disabled={getUnitCount(player.id, 'Knight') === 0}
                >
                  -
                </button>
                <span className="count">{getUnitCount(player.id, 'Knight')}</span>
                <button onClick={() => handleIncrement(player.id, 'Knight')}>+</button>
              </div>
            </div>

            <div className="unit-counter">
              <div className="unit-label">
                <span className="unit-icon">🏹</span>
                <span>Archers</span>
                <span className="unit-info">({gameState.unitStats['Archer'].cost} pts)</span>
              </div>
              <div className="counter-controls">
                <button
                  onClick={() => handleDecrement(player.id, 'Archer')}
                  disabled={getUnitCount(player.id, 'Archer') === 0}
                >
                  -
                </button>
                <span className="count">{getUnitCount(player.id, 'Archer')}</span>
                <button onClick={() => handleIncrement(player.id, 'Archer')}>+</button>
              </div>
            </div>

            <div className="unit-counter">
              <div className="unit-label">
                <span className="unit-icon">🎯</span>
                <span>Crossbows</span>
                <span className="unit-info">({gameState.unitStats['Crossbow'].cost} pts)</span>
              </div>
              <div className="counter-controls">
                <button
                  onClick={() => handleDecrement(player.id, 'Crossbow')}
                  disabled={getUnitCount(player.id, 'Crossbow') === 0}
                >
                  -
                </button>
                <span className="count">{getUnitCount(player.id, 'Crossbow')}</span>
                <button onClick={() => handleIncrement(player.id, 'Crossbow')}>+</button>
              </div>
            </div>

            <div className="unit-counter">
              <div className="unit-label">
                <span className="unit-icon">✚</span>
                <span>Healers</span>
                <span className="unit-info">({gameState.unitStats['Healer'].cost} pts)</span>
              </div>
              <div className="counter-controls">
                <button
                  onClick={() => handleDecrement(player.id, 'Healer')}
                  disabled={getUnitCount(player.id, 'Healer') === 0}
                >
                  -
                </button>
                <span className="count">{getUnitCount(player.id, 'Healer')}</span>
                <button onClick={() => handleIncrement(player.id, 'Healer')}>+</button>
              </div>
            </div>

            <div className="unit-counter">
              <div className="unit-label">
                <span className="unit-icon">🏛️</span>
                <span>Siege Equipment</span>
                <span className="unit-info">({gameState.unitStats['Siege Equipment'].cost} pts)</span>
              </div>
              <div className="counter-controls">
                <button
                  onClick={() => handleDecrement(player.id, 'Siege Equipment')}
                  disabled={getUnitCount(player.id, 'Siege Equipment') === 0}
                >
                  -
                </button>
                <span className="count">{getUnitCount(player.id, 'Siege Equipment')}</span>
                <button onClick={() => handleIncrement(player.id, 'Siege Equipment')}>+</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-container">
      <h1>Admin Area</h1>

      <div className="admin-section">
        <h2>Add New Player</h2>
        <div className="add-player-form">
          <input
            type="text"
            placeholder="Player Name"
            value={newPlayerName}
            onChange={(e) => setNewPlayerName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddPlayer()}
          />
          <select value={newPlayerTeam} onChange={(e) => setNewPlayerTeam(e.target.value as Team)}>
            <option value="raiders">Raiders</option>
            <option value="defenders">Defenders</option>
          </select>
          <button onClick={handleAddPlayer}>Add Player</button>
        </div>
      </div>

      <div className="admin-section">
        <h2>Battlefield Settings</h2>
        <div className="battlefield-settings-form">
          <div className="input-group">
            <label>Background Image URL:</label>
            <input
              type="text"
              placeholder="https://example.com/battlefield-bg.jpg"
              value={battlefieldBgUrl}
              onChange={(e) => setBattlefieldBgUrl(e.target.value)}
            />
            <button
              onClick={handleUpdateBattlefieldBackground}
              disabled={isSavingBg}
            >
              {isSavingBg ? 'Saving...' : 'Update Background'}
            </button>
          </div>
          {battlefieldBgUrl && (
            <div className="preview-hint">
              Current: {battlefieldBgUrl}
            </div>
          )}
        </div>
      </div>

      <div className="admin-section">
        <h2>King Images</h2>
        <div className="battlefield-settings-form">
          <div className="input-group">
            <label>Raiders King Image URL:</label>
            <input
              type="text"
              placeholder="https://example.com/raiders-king.png"
              value={kingRaidersUrl}
              onChange={(e) => setKingRaidersUrl(e.target.value)}
            />
          </div>
          <div className="input-group">
            <label>Defenders King Image URL:</label>
            <input
              type="text"
              placeholder="https://example.com/defenders-king.png"
              value={kingDefendersUrl}
              onChange={(e) => setKingDefendersUrl(e.target.value)}
            />
          </div>
          <button
            onClick={handleUpdateKingImages}
            disabled={isSavingKings}
          >
            {isSavingKings ? 'Saving...' : 'Update King Images'}
          </button>
          {(kingRaidersUrl || kingDefendersUrl) && (
            <div className="preview-hint">
              {kingRaidersUrl && <div>Raiders: {kingRaidersUrl}</div>}
              {kingDefendersUrl && <div>Defenders: {kingDefendersUrl}</div>}
            </div>
          )}
        </div>
      </div>

      <div className="teams-columns">
        <div className="team-column raiders-column">
          <div className="team-header raiders">
            <h2>Raiders</h2>
            <div className="manpower-summary">
              <span>{raidersManpower.players} Players</span>
              <span>{raidersManpower.units} Units</span>
              <span className="stat-display">HP: {raidersStats.hp}</span>
              <span className="stat-display">Damage: {raidersStats.damage}</span>
            </div>
          </div>
          <div className="players-list">
            {raiders.map(player => renderPlayerCard(player))}
          </div>
        </div>

        <div className="team-column defenders-column">
          <div className="team-header defenders">
            <h2>Defenders</h2>
            <div className="manpower-summary">
              <span>{defendersManpower.players} Players</span>
              <span>{defendersManpower.units} Units</span>
              <span className="stat-display">HP: {defendersStats.hp}</span>
              <span className="stat-display">Damage: {defendersStats.damage}</span>
            </div>
          </div>
          <div className="players-list">
            {defenders.map(player => renderPlayerCard(player))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
